// @flow

import React, {type AbstractComponent} from 'react';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {type LoadByHashidInjectedProps} from 'app/general/loadByHashid';
import {objectsToFlatArray} from 'app/utils/redux';
import {type StoreShapeType} from 'app/reducers/types';

/*
    this enhancer should load object for the page using the hashid
    provided in the route URL
 */

const getObjectVersions = (state, objId, storeKey, storePath) =>
  state[storeKey].getIn(storePath.concat([objId.toString()]));

export const versionsFetcherSelector = createSelector<
  StoreShapeType,
  number,
  any,
  any
>(
  (state, objId, storeKey, storePath) =>
    getObjectVersions(state, objId, storeKey, storePath),
  (versions) => versions && objectsToFlatArray(versions)
);

export const versionFetcherSelector = createSelector<
  string,
  StoreShapeType,
  any,
  any,
  any
>(
  (versionid) => versionid,
  (versionid, state, objId, storeKey, storePath) =>
    getObjectVersions(state, objId, storeKey, storePath),
  (versionid, versions) => {
    if (versionid == null || versions == null) {
      return null;
    }
    const version = versions.getIn(['byId', versionid.toString()]);
    return version != null ? version.toJS() : null;
  }
);

export type LoadVersionsInjectedProps<V> = {|
  fetchedVersions: {
    fetchVersions: () => Promise<any>,
    objectVersion: V | null,
    objectVersions: Array<V>,
  },
|};

const defaultFetchParamBuilder = (props: any) =>
  props.fetchedByHashid.object.id;

type LoadVersionsOptions = {|
  fetchVersionsAction: (any) => any,
  storeMap: [string, Array<string>],
  LoadingComponent: AbstractComponent<any>,
  fetchParamBuilder?: (any) => string | number,
  waitForObjectVersion?: boolean,
|};

export default function loadVersions({
  fetchVersionsAction,
  storeMap,
  LoadingComponent,
  fetchParamBuilder = defaultFetchParamBuilder,
  waitForObjectVersion = false,
}: LoadVersionsOptions) {
  const [storeKey, storePath] = storeMap;
  type RequiredProps = {
    ...LoadByHashidInjectedProps<any>,
    params: {
      versionid: any,
      hashid: string,
    },
  };

  return function <Config: RequiredProps, V>(
    WrappedComponent: AbstractComponent<{|
      ...$Exact<Config>,
      ...LoadVersionsInjectedProps<V>,
    |}>
  ): AbstractComponent<Config> {
    type OwnProps = {|
      ...$Exact<Config>,
    |};

    type StateProps = {|
      objectVersion: V | null,
      objectVersions: Array<V> | null,
    |};

    type DispatchProps = {|
      fetchVersionsAction: (objectId: number | string) => any,
    |};

    type Props = {|
      ...OwnProps,
      ...StateProps,
      ...DispatchProps,
    |};

    class ResolverComponent extends React.PureComponent<Props> {
      UNSAFE_componentWillMount() {
        if (this.props.fetchedByHashid.object != null) {
          this.fetchVersions();
        }
      }

      fetchVersions = () => {
        const param = fetchParamBuilder(this.props);
        return this.props.fetchVersionsAction(param);
      };

      UNSAFE_componentWillReceiveProps(nextProps: Props) {
        const currObject = this.props.fetchedByHashid.object;
        const nextObject = nextProps.fetchedByHashid.object;

        if (
          (currObject == null && nextObject != null) ||
          nextProps.params.versionid !== this.props.params.versionid
        ) {
          nextProps.fetchVersionsAction(fetchParamBuilder(nextProps));
        }
      }

      render() {
        // eslint-disable-next-line no-unused-vars
        const {fetchVersionsAction, objectVersion, objectVersions, ...other} =
          this.props;

        return objectVersions != null &&
          (!waitForObjectVersion || objectVersion != null) ? (
          <WrappedComponent
            fetchedVersions={{
              fetchVersions: this.fetchVersions,
              objectVersion,
              objectVersions,
            }}
            {...other}
          />
        ) : (
          <LoadingComponent
            fetchedVersions={{
              fetchVersions: this.fetchVersions,
              objectVersion,
              objectVersions,
            }}
            {...other}
          />
        );
      }
    }

    const mapStateToProps = (
      state: StoreShapeType,
      {fetchedByHashid: {object}, params}
    ): StateProps => {
      const {versionid} = params;

      const objectVersions =
        object != null
          ? versionsFetcherSelector(state, object.id, storeKey, storePath)
          : null;

      const objectVersion =
        objectVersions != null
          ? versionFetcherSelector(
              versionid,
              state,
              object.id,
              storeKey,
              storePath
            )
          : null;

      return {objectVersions, objectVersion};
    };

    return connect<Props, Config, StateProps, DispatchProps, StoreShapeType, _>(
      mapStateToProps,
      {fetchVersionsAction}
    )(ResolverComponent);
  };
}
